// "Viewing as" — the admin act-as-client context.
//
// WHY A MODULE STORE AND NOT A REACT CONTEXT: authedFetch is a plain async
// function, not a component, so it cannot read context. Everything that needs
// this state either is a component (and uses the useViewingAs hook, which
// subscribes here) or is not (and calls getViewingAs directly). A Provider
// would have solved only half the problem and still needed a module-level
// mirror for the fetch helper — so there is one source of truth, here.
//
// This also matches how the codebase already shares auth state: useClientId is
// a hook over module-level capture, not a Provider. No Provider is added to
// the tree by this file.
//
// IN-MEMORY ONLY — deliberately no localStorage, no sessionStorage.
// The Sep 7 decision was that the admin stays authenticated as themselves and
// this is UI state, not a session swap. Persisting it would give it the one
// property a session has and this must not: surviving a reload. An admin who
// closes the tab, refreshes, or comes back tomorrow is themselves again with
// no action required, and a forgotten viewing-as context cannot outlive the
// page it was set on.
//
// NOT A PERMISSION. Setting this grants nothing. Every request that carries
// the override is re-checked by requireOwnerOrAdmin against ADMIN_USER_IDS on
// the server; a non-admin who set this state by any means gets 403s and
// nothing else. Never treat a non-null viewingAsClientId as proof of anything.

const EMPTY = Object.freeze({ viewingAsClientId: null, viewingAsClientName: null });

// Replaced wholesale rather than mutated: useSyncExternalStore compares
// snapshots by reference, so an in-place edit would not re-render.
let state = EMPTY;

const listeners = new Set();

function emit() {
  for (const fn of listeners) {
    try { fn(); } catch (err) { console.error('[viewingAs] listener failed:', err); }
  }
}

export function getViewingAs() {
  return state;
}

export function setViewingAs(clientId, clientName = null) {
  if (!clientId) return clearViewingAs();
  if (state.viewingAsClientId === clientId && state.viewingAsClientName === clientName) return;
  state = Object.freeze({ viewingAsClientId: clientId, viewingAsClientName: clientName });
  emit();
}

export function clearViewingAs() {
  if (state === EMPTY) return;
  state = EMPTY;
  emit();
}

export function subscribeViewingAs(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
